import type { ReactNode } from 'react'
import clsx from 'clsx'
import { CircleAlert, CircleCheck, CircleMinus, Info, TriangleAlert } from 'lucide-react'
import { BOOKING_STATUS_LABELS, type BookingStatus } from '@shared/constants'

/*
 * NOTHING ANIMATED LIVES IN THIS FILE, by design. `router.tsx` renders
 * `LoadingBlock` as its Suspense fallback, which makes this module part of the
 * entry chunk that every visitor downloads — including a diner opening the
 * public booking page. `StatCard`, `StatGrid` and the digit animation they
 * carry are in `Stat.tsx`, where only the authenticated dashboard pays for
 * them.
 */

/** Cards, badges, stats and the empty/loading/error states used everywhere. */

export function Card({
  children,
  className,
  as: Tag = 'div',
}: {
  children: ReactNode
  className?: string
  as?: 'div' | 'section' | 'article' | 'li'
}) {
  return (
    <Tag
      className={clsx(
        'rounded-[var(--radius-lg)] border border-line bg-surface shadow-xs',
        className,
      )}
    >
      {children}
    </Tag>
  )
}

export function CardHeader({
  title,
  description,
  action,
  className,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div
      className={clsx(
        // Tighter than the body it introduces. A card header set at the same
        // size and rhythm as the rows below it makes every card look like a
        // list that starts one item early.
        'flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b border-line px-4 py-3 sm:px-5',
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        {description ? (
          <p className="mt-0.5 text-[13px] leading-relaxed text-ink-muted">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
  meta,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  breadcrumb?: ReactNode
  /**
   * A line of figures that belongs to the page rather than to any card on it —
   * "17 covers · 6 bookings". Sitting it under the title stops it drifting
   * about the page as an orphan paragraph between two unrelated controls.
   */
  meta?: ReactNode
}) {
  return (
    // The action sits BESIDE the title at every width, not stacked under it.
    // "Today" and "New booking" fit together on a 360px phone with room to
    // spare; giving the button a row of its own cost 60px of the first screen
    // of a service and made a secondary action look like a banner. If the
    // actions genuinely cannot fit, flex-wrap drops them to their own line.
    <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
      {/*
        `basis-[15rem]` is what stops the title being crushed. With only
        `flex-1` the title kept whatever the buttons left over — on the guests
        screen at 390px that was a 110px column, and "Everyone who has booked,
        built from your own reservations" wrapped over seven lines beside two
        buttons. Given a minimum width to ask for, the actions wrap onto their
        own line instead, which is the correct outcome at that size.
      */}
      <div className="min-w-0 flex-1 basis-[11rem]">
        {breadcrumb ? <div className="mb-1.5">{breadcrumb}</div> : null}
        {/*
          The one serif in the product.

          It is here and nowhere else on purpose. A page title is the only
          piece of type on a dashboard that is read once, on arrival, to answer
          "where am I" — everything below it is furniture and data, which is
          Inter's job and which a display face would actively damage. Confining
          Instrument Serif to this line gives the product a voice at the moment
          somebody looks up, and keeps it out of every moment they are working.

          An explicit px size rather than `text-2xl`, because the size steps
          carry their own letter-spacing and would fight the tracking the serif
          needs. Weight stays 400: the family has no other, and asking for 600
          would get a synthesised bold, which on strokes this fine looks like a
          printing fault.
        */}
        <h1 className="font-display text-[26px] text-ink sm:text-[30px]">{title}</h1>
        {description ? (
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-ink-muted">{description}</p>
        ) : null}
        {meta ? <div className="mt-1.5 text-[13px] text-ink-muted">{meta}</div> : null}
      </div>
      {/*
        `min-w-0`, not `shrink-0`. The diary puts a search box in here, and a
        non-shrinking box whose max-content is 450px pushed 96px of horizontal
        overflow onto a 390px phone — the one thing this codebase has an
        end-to-end test to prevent. Buttons carry `whitespace-nowrap`, so they
        still refuse to squash; anything that CAN absorb the space, does.
      */}
      {actions ? <div className="flex min-w-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  )
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

export function Badge({
  children,
  tone = 'neutral',
  className,
  title,
}: {
  children: ReactNode
  tone?: 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info'
  className?: string
  /** Native tooltip for badges whose text may be truncated. */
  title?: string
}) {
  const tones = {
    neutral: 'bg-surface-sunken text-ink-muted',
    brand: 'bg-brand-50 text-brand-800',
    success: 'bg-success-50 text-success-600',
    warning: 'bg-warning-50 text-warning-700',
    danger: 'bg-danger-50 text-danger-700',
    info: 'bg-info-50 text-info-600',
  }[tone]

  return (
    <span
      title={title}
      className={clsx(
        // Badges annotate; they are not headlines. A shade smaller and a
        // touch tighter keeps a row of three of them from out-shouting the
        // guest's name they are attached to.
        'inline-flex max-w-full items-center gap-1 rounded-full px-2 py-[3px] text-[11.5px] font-semibold leading-none',
        tones,
        className,
      )}
    >
      {children}
    </span>
  )
}

/**
 * Booking status.
 *
 * Colour is never the only signal: each status also has a distinct SHAPE
 * marker and an always-visible text label, so the diary is readable with any
 * form of colour vision and in a printed service sheet.
 */
const STATUS_STYLE: Record<
  BookingStatus,
  { bg: string; fg: string; marker: 'dot' | 'ring' | 'square' | 'check' | 'cross' | 'slash' }
> = {
  pending: { bg: 'bg-status-pending-bg', fg: 'text-status-pending-fg', marker: 'ring' },
  confirmed: { bg: 'bg-status-confirmed-bg', fg: 'text-status-confirmed-fg', marker: 'check' },
  seated: { bg: 'bg-status-seated-bg', fg: 'text-status-seated-fg', marker: 'dot' },
  completed: { bg: 'bg-status-completed-bg', fg: 'text-status-completed-fg', marker: 'square' },
  cancelled: { bg: 'bg-status-cancelled-bg', fg: 'text-status-cancelled-fg', marker: 'slash' },
  no_show: { bg: 'bg-status-noshow-bg', fg: 'text-status-noshow-fg', marker: 'cross' },
}

function StatusMarker({ marker }: { marker: string }) {
  const common = 'h-[9px] w-[9px] shrink-0'
  switch (marker) {
    case 'check':
      return (
        <svg viewBox="0 0 12 12" className={common} fill="none" aria-hidden="true">
          <path d="M2 6.5 4.8 9 10 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'ring':
      return (
        <svg viewBox="0 0 12 12" className={common} fill="none" aria-hidden="true">
          <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="2" />
        </svg>
      )
    case 'square':
      return (
        <svg viewBox="0 0 12 12" className={common} aria-hidden="true">
          <rect x="2" y="2" width="8" height="8" rx="1.5" fill="currentColor" />
        </svg>
      )
    case 'cross':
      return (
        <svg viewBox="0 0 12 12" className={common} fill="none" aria-hidden="true">
          <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      )
    case 'slash':
      return (
        <svg viewBox="0 0 12 12" className={common} fill="none" aria-hidden="true">
          <path d="M2.5 9.5 9.5 2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      )
    default:
      return (
        <svg viewBox="0 0 12 12" className={common} aria-hidden="true">
          <circle cx="6" cy="6" r="3.5" fill="currentColor" />
        </svg>
      )
  }
}

export function StatusBadge({
  status,
  className,
}: {
  status: BookingStatus
  className?: string
}) {
  const style = STATUS_STYLE[status]
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-[3px] text-[11.5px] font-semibold leading-none',
        style.bg,
        style.fg,
        className,
      )}
    >
      <StatusMarker marker={style.marker} />
      {BOOKING_STATUS_LABELS[status]}
    </span>
  )
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('skeleton rounded-[var(--radius-md)]', className)} aria-hidden="true" />
}

/**
 * Placeholder for content that is on its way.
 *
 * Three identical 80px slabs told the reader nothing except that something was
 * happening. A skeleton earns its place by holding the SHAPE of what is about
 * to arrive: rows of a list, a strip of figures, a form. Getting that roughly
 * right means the page does not jump when the data lands, and the eye has
 * already found the column it was going to read.
 */
export function LoadingBlock({
  label = 'Loading',
  variant = 'rows',
  rows = 4,
}: {
  label?: string
  variant?: 'rows' | 'stats' | 'panel'
  rows?: number
}) {
  return (
    <div role="status" aria-live="polite" className="min-w-0">
      <span className="sr-only">{label}</span>

      {variant === 'stats' ? (
        // `lg:`, matching StatGrid's own breakpoint: with `sm:` the skeleton
        // was one row of four between 640 and 1024px while the content it
        // stood in for arrived as a 2x2 grid, so the page jumped on load.
        <div className="rule-grid grid-cols-2 overflow-hidden rounded-[var(--radius-lg)] border border-line lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="bg-surface px-4 py-3.5">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="mt-2.5 h-6 w-12" />
            </div>
          ))}
        </div>
      ) : null}

      {variant === 'rows' ? (
        <ul className="overflow-hidden rounded-[var(--radius-lg)] border border-line bg-surface">
          {Array.from({ length: rows }, (_, index) => (
            <li
              key={index}
              className="flex items-center gap-3 border-b border-line px-4 py-3.5 last:border-b-0"
            >
              <Skeleton className="h-7 w-11 shrink-0" />
              <div className="min-w-0 flex-1">
                {/* Varying the widths stops the block reading as a rendering
                    fault and matches the rag of real names. */}
                <Skeleton className={index % 2 === 0 ? 'h-3.5 w-40' : 'h-3.5 w-28'} />
                <Skeleton className="mt-2 h-3 w-20" />
              </div>
              <Skeleton className="h-5 w-16 shrink-0 rounded-full" />
            </li>
          ))}
        </ul>
      ) : null}

      {variant === 'panel' ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-5 w-44" />
          <Skeleton className="h-3.5 w-28" />
          <div className="rule-grid grid-cols-3 overflow-hidden rounded-[var(--radius-md)] border border-line">
            {Array.from({ length: 3 }, (_, index) => (
              <div key={index} className="bg-surface px-3 py-2.5">
                <Skeleton className="h-2.5 w-10" />
                <Skeleton className="mt-2 h-4 w-12" />
              </div>
            ))}
          </div>
          <Skeleton className="h-11 w-full" />
        </div>
      ) : null}
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string
  description?: ReactNode
  action?: ReactNode
  icon?: ReactNode
}) {
  return (
    <div
      // Lets the dark widget repaint this without the component knowing about
      // themes — see the `[data-form-tone='dark']` block in index.css.
      data-surface="empty"
      className={clsx(
        // Was a dashed outline on white. A dashed box says "drop a file here";
        // inside a card it also draws a second frame one padding-width in from
        // the first. A quiet sunken panel reads as absence instead.
        'flex flex-col items-center justify-center gap-2 rounded-[var(--radius-lg)]',
        'border border-line bg-surface-sunken px-6 py-9 text-center',
      )}
    >
      {icon ? (
        <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-surface text-ink-subtle shadow-xs">
          {icon}
        </div>
      ) : null}
      <h3 className="text-[15px] font-semibold text-ink">{title}</h3>
      {description ? (
        <p className="max-w-[38ch] text-[13px] leading-relaxed text-ink-muted">{description}</p>
      ) : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  )
}

export function ErrorState({
  title = 'Something went wrong',
  description,
  onRetry,
  retryLabel = 'Try again',
}: {
  title?: string
  description?: ReactNode
  onRetry?: () => void
  retryLabel?: string
}) {
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-3 rounded-[var(--radius-lg)] border border-danger-100 bg-danger-50 px-4 py-3.5 sm:px-5"
    >
      <div className="flex items-start gap-2.5">
        <CircleAlert
          className="mt-px h-[18px] w-[18px] shrink-0 text-danger-600"
          strokeWidth={2}
          aria-hidden="true"
        />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-danger-700">{title}</h3>
          {description ? (
            <p className="mt-0.5 text-[13px] leading-relaxed text-danger-700/90">{description}</p>
          ) : null}
        </div>
      </div>
      {onRetry ? (
        // The product-wide recovery control, so it follows the product-wide
        // button rules: 44px under a finger, tighter under a cursor, and a
        // press that answers. It was a 32px hand-rolled button — smallest
        // exactly where somebody is already having a bad time.
        <button
          type="button"
          onClick={onRetry}
          className="press ml-[26px] min-h-11 rounded-[var(--radius-md)] border border-danger-500 px-3 text-[13px] font-semibold text-danger-700 hover:bg-danger-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700 [@media(pointer:fine)]:min-h-8"
        >
          {retryLabel}
        </button>
      ) : null}
    </div>
  )
}

export function InlineNotice({
  tone = 'info',
  title,
  children,
  action,
  className,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success'
  title?: string
  children: ReactNode
  action?: ReactNode
  className?: string
}) {
  const tones = {
    info: 'border-info-600/20 bg-info-50 text-info-600',
    warning: 'border-warning-600/25 bg-warning-50 text-warning-700',
    danger: 'border-danger-500/25 bg-danger-50 text-danger-700',
    success: 'border-success-600/25 bg-success-50 text-success-600',
  }[tone]

  return (
    <div
      data-surface="notice"
      data-surface-tone={`notice-${tone}`}
      className={clsx(
        'flex flex-col gap-2 rounded-[var(--radius-lg)] border px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4',
        tones,
        className,
      )}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      {/* The glyph differs per tone, so the meaning survives greyscale, a
          printed service sheet and any form of colour vision — the same rule
          the status badges follow. */}
      <div className="flex min-w-0 items-start gap-2.5 text-[13px] leading-relaxed">
        <NoticeIcon tone={tone} />
        <div className="min-w-0">
          {title ? <p className="font-semibold">{title}</p> : null}
          <div className={clsx(title && 'mt-0.5')}>{children}</div>
        </div>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

/**
 * The same four glyphs the toast layer uses, from the same set. They were
 * hand-drawn filled circles here and lucide outlines there — the identical
 * meaning wearing two different faces one screen apart. Lucide is already in
 * the entry chunk via ToastProvider, so this costs nothing.
 */
function NoticeIcon({ tone }: { tone: 'info' | 'warning' | 'danger' | 'success' }) {
  const common = 'mt-px h-4 w-4 shrink-0'
  if (tone === 'success') {
    return <CircleCheck className={common} strokeWidth={2} aria-hidden="true" />
  }
  if (tone === 'warning') {
    return <TriangleAlert className={common} strokeWidth={2} aria-hidden="true" />
  }
  if (tone === 'danger') {
    return <CircleMinus className={common} strokeWidth={2} aria-hidden="true" />
  }
  return <Info className={common} strokeWidth={2} aria-hidden="true" />
}
